import {
    AccountInfo,
    AuthenticationResult,
    Configuration,
    InteractionRequiredAuthError,
    PublicClientApplication,
    RedirectRequest,
    SilentRequest,
} from '@azure/msal-browser'
import { jwtDecode } from 'jwt-decode'
import { environment } from '@/config/environment'

const ADMIN_ROLE = 'IIOT.Admin'

type RoleToken = {
    roles?: string[]
}

export const authRequest: RedirectRequest = {
    scopes: ['openid'],
}

export const msalConfig: Configuration = {
    auth: {
        clientId: environment.AD_CLIENT_ID,
        authority: `https://login.microsoftonline.com/${environment.AD_TENANT_ID}`,
        redirectUri: window.location.origin,
    },
    cache: {
        cacheLocation: 'sessionStorage',
    },
}

export const msalInstance = new PublicClientApplication(msalConfig)

export async function initializeMsal(): Promise<void> {
    await msalInstance.initialize()
}

export const getAccountInfo = (): AccountInfo | null => {
    const allAccounts = msalInstance.getAllAccounts()

    if (allAccounts.length < 1) {
        return null
    }

    if (allAccounts.length > 1) {
        return msalInstance.getActiveAccount()
    }

    return allAccounts[0]
}

export async function fetchAccessToken(scopes: string[]): Promise<string> {
    const accounts = msalInstance.getAllAccounts()

    if (accounts.length === 0) {
        return ''
    }

    const request: SilentRequest = {
        scopes,
        account: accounts[0],
    }

    try {
        const response: AuthenticationResult = await msalInstance.acquireTokenSilent(request)
        return response.accessToken
    } catch (error: unknown) {
        if (error instanceof InteractionRequiredAuthError) {
            await msalInstance.acquireTokenRedirect({
                scopes,
                account: accounts[0],
            })
            return ''
        }

        console.error(error)
        return ''
    }
}

export async function hasCurrentUserRole(role: string): Promise<boolean> {
    const accessToken = await fetchAccessToken(environment.BACKEND_API_SCOPE)
    if (!accessToken) return false

    const payload = jwtDecode<RoleToken>(accessToken)
    return payload?.roles?.includes(role) ?? false
}

export async function isCurrentUserAdmin(): Promise<boolean> {
    return hasCurrentUserRole(ADMIN_ROLE)
}

export async function signOut(): Promise<void> {
    type TokenClaims = {
        login_hint?: string
    }

    const activeAccount = getAccountInfo()
    if (!activeAccount) return

    const claims = activeAccount.idTokenClaims as TokenClaims | undefined

    await msalInstance.logoutRedirect({
        account: activeAccount,
        logoutHint: claims?.login_hint,
        postLogoutRedirectUri: undefined,
    })
}
